// ---------------------------------------------------------------------------
// บัญชีผู้ใช้ใน RTDB: /users/<uid>
//  plan:   { id, cycle: "monthly"|"yearly", startedAt, expiresAt }       (ไม่มี/หมดอายุ = free)
//  quota:  { period: "YYYY-MM", used }                                      โควตาของแพ็กเกจ รีเซ็ตทุกเดือน
//  wallet: token ที่ซื้อแบบจ่ายตามการใช้งาน (ไม่หมดอายุ)
//  หักเงินด้วย transaction (ETag) ที่ /users/<uid> — หักโควตาก่อน แล้วค่อยหักกระเป๋า
//  ประวัติใช้งาน /usage/<uid>/<id> · สถิติรวม /stats/daily/<date>
// ---------------------------------------------------------------------------
import { db, fk, nowIso, newId, monthKey, todayKey } from "./db";
import { getPricing, planById, customPrice, paygTokens, validPaygAmount, CUSTOM_PLAN_ID } from "./pricing";

export const SIGNUP_BONUS = 10000;

const isActivePlan = (u) => !!u?.plan?.id && u.plan.id !== "free" && (!u.plan.expiresAt || new Date(u.plan.expiresAt) > new Date());

/** สรุปยอดคงเหลือ (คำนวณจากแถวผู้ใช้ + ราคาปัจจุบัน) */
export function balanceOf(u, pricing) {
  const planId = isActivePlan(u) ? u.plan.id : "free";
  // แพ็กเกจกำหนดเอง: โควตาเก็บในแถวผู้ใช้ (ตอนซื้อ) ไม่ได้อยู่ในรายการแพ็กเกจ
  const plan = planId === CUSTOM_PLAN_ID ? { id: CUSTOM_PLAN_ID, name: "Custom", tokens: Number(u.plan.tokens) || 0 } : planById(pricing, planId);
  const used = u?.quota?.period === monthKey() ? Number(u.quota.used) || 0 : 0;
  const limit = Number(plan.tokens) || 0;
  const quotaLeft = Math.max(0, limit - used);
  const wallet = Math.max(0, Number(u?.wallet) || 0);
  return { planId, plan, cycle: planId === "free" ? null : u.plan.cycle, expiresAt: planId === "free" ? null : u.plan.expiresAt, limit, used, quotaLeft, wallet, total: quotaLeft + wallet };
}

const bumpStats = (patch) =>
  db
    .transaction(`stats/daily/${todayKey()}`, (cur) => {
      const c = cur || {};
      const next = { ...c };
      Object.entries(patch).forEach(([k, v]) => (next[k] = (Number(c[k]) || 0) + v));
      return next;
    })
    .catch(() => {});

export const users = {
  get: (uid) => db.get(`users/${fk(uid)}`),

  /** สร้างแถวผู้ใช้ครั้งแรก (+โบนัสสมัคร) หรืออัปเดตเวลาเข้าใช้ */
  async ensure(identity) {
    const path = `users/${fk(identity.uid)}`;
    const now = nowIso();
    const created = await db.createOnly(path, {
      id: identity.uid, email: identity.email, displayName: identity.displayName, userType: identity.user_type,
      plan: { id: "free" }, quota: { period: monthKey(), used: 0 }, wallet: SIGNUP_BONUS, totalUsed: 0, messages: 0,
      banned: false, createdAt: now, lastLoginAt: now,
    });
    if (created) {
      await db.put(`wallet_tx/${fk(identity.uid)}/${newId("w")}`, { type: "bonus", tokens: SIGNUP_BONUS, note: "โบนัสต้อนรับสมาชิกใหม่", at: now });
      bumpStats({ signups: 1 });
    } else {
      await db.patch(path, { lastLoginAt: now, userType: identity.user_type, email: identity.email });
    }
    const u = await db.get(path);
    if (u?.banned) throw new Error("บัญชีนี้ถูกระงับการใช้งาน" + (u.banReason ? ": " + u.banReason : ""));
    return u;
  },

  async updateProfile(uid, { displayName }) {
    await db.patch(`users/${fk(uid)}`, { displayName: String(displayName || "").slice(0, 40) });
  },

  /** เช็คก่อนส่งข้อความ: ต้องมี token เหลือ */
  async canSpend(uid) {
    const [u, pricing] = await Promise.all([users.get(uid), getPricing()]);
    if (!u) return { ok: false, message: "ไม่พบบัญชี กรุณาเข้าสู่ระบบใหม่" };
    if (u.banned) return { ok: false, message: "บัญชีนี้ถูกระงับการใช้งาน" };
    const b = balanceOf(u, pricing);
    if (b.total <= 0) return { ok: false, code: "no_tokens", message: "token หมดแล้ว — อัปเกรดแพ็กเกจหรือซื้อ token เพิ่มเพื่อใช้งานต่อ" };
    return { ok: true, balance: b };
  },

  /**
   * หัก token หลังได้คำตอบ: cost = (prompt + completion) × rate
   * หักโควตาแพ็กเกจก่อน แล้วค่อยหักกระเป๋า (ถ้าไม่พอหักเท่าที่เหลือ — คำตอบสร้างไปแล้ว ไม่ทิ้ง)
   */
  async charge(uid, { usage, model, kind = "chat", ref = "" }) {
    const pricing = await getPricing();
    const tokens = (usage?.prompt || 0) + (usage?.completion || 0);
    const cost = Math.max(1, Math.ceil(tokens * (Number(model?.rate) || 1)));
    let fromQuota = 0;
    let fromWallet = 0;
    const res = await db.transaction(`users/${fk(uid)}`, (u) => {
      if (!u) return undefined;
      const b = balanceOf(u, pricing);
      fromQuota = Math.min(cost, b.quotaLeft);
      fromWallet = Math.min(cost - fromQuota, b.wallet);
      return {
        ...u,
        quota: { period: monthKey(), used: b.used + fromQuota },
        wallet: b.wallet - fromWallet,
        totalUsed: (Number(u.totalUsed) || 0) + fromQuota + fromWallet,
        messages: (Number(u.messages) || 0) + 1,
        lastActiveAt: nowIso(),
      };
    });
    if (!res.committed) return null;
    const row = { kind, model: model?.id || "", modelName: model?.name || "", prompt: usage?.prompt || 0, completion: usage?.completion || 0, rate: Number(model?.rate) || 1, cost, fromQuota, fromWallet, ref, at: nowIso() };
    await db.put(`usage/${fk(uid)}/${newId("us")}`, row);
    bumpStats({ tokens: fromQuota + fromWallet, messages: 1 });
    return { ...row, balance: balanceOf(res.value, pricing) };
  },

  async usage(uid, limit = 50) {
    const rows = await db.entries(`usage/${fk(uid)}`, { orderBy: "$key", limitToLast: limit });
    return rows.map(([id, v]) => ({ id, ...v })).reverse();
  },

  async walletTx(uid, limit = 50) {
    const rows = await db.entries(`wallet_tx/${fk(uid)}`, { orderBy: "$key", limitToLast: limit });
    return rows.map(([id, v]) => ({ id, ...v })).reverse();
  },

  async payments(uid) {
    const rows = await db.entries("payments", { orderBy: "userId", equalTo: uid });
    return rows.map(([id, v]) => ({ id, ...v })).sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  },

  /**
   * บันทึกการชำระเงินที่ PaymentBroker ยืนยันแล้ว — idempotent ด้วย payments/<transactionId> (createOnly)
   * order: { kind: "subscription", planId, cycle } | { kind: "tokens", packId }
   */
  async applyPayment(uid, { transactionId, amount, order, email }) {
    const pricing = await getPricing({ fresh: true });
    const now = new Date();
    const rec = { id: transactionId, userId: uid, email: email || "", amount: Number(amount) || 0, ...order, status: "paid", createdAt: now.toISOString() };
    // คิดราคาที่ควรจ่ายใหม่จากราคาปัจจุบัน — ยอดที่จ่ายจริงต้องไม่น้อยกว่านี้ (กันแก้ order ฝั่งหน้าเว็บ)
    let expected = 0;
    if (order.kind === "subscription" && order.planId === CUSTOM_PLAN_ID) {
      const t = Number(order.tokens) || 0;
      if (t < pricing.custom.minTokens || t > pricing.custom.maxTokens) throw new Error("โควตาแพ็กเกจกำหนดเองไม่ถูกต้อง");
      expected = customPrice(pricing, t, order.cycle);
      rec.itemName = `แพ็กเกจ Custom ${(t / 1e6).toLocaleString("th-TH")}M token/เดือน (${order.cycle === "yearly" ? "รายปี" : "รายเดือน"})`;
      rec.tokens = t;
    } else if (order.kind === "subscription") {
      const plan = planById(pricing, order.planId);
      if (plan.id !== order.planId || plan.id === "free") throw new Error("ไม่พบแพ็กเกจนี้");
      expected = order.cycle === "yearly" ? plan.yearly : plan.monthly;
      rec.itemName = `แพ็กเกจ ${plan.name} (${order.cycle === "yearly" ? "รายปี" : "รายเดือน"})`;
      rec.tokens = plan.tokens;
    } else {
      const amt = Number(order.amount);
      if (!validPaygAmount(pricing, amt)) throw new Error("ยอดซื้อ token ไม่อยู่ในช่วงที่กำหนด");
      expected = amt;
      rec.tokens = paygTokens(pricing, amt);
      rec.itemName = `เติม ${rec.tokens.toLocaleString("th-TH")} token`;
    }
    if (rec.amount + 0.001 < expected) throw new Error(`ยอดชำระ (${rec.amount}) น้อยกว่าราคา (${expected}) — กรุณาติดต่อทีมงานพร้อมเลข Ref`);
    const fresh = await db.createOnly(`payments/${fk(transactionId)}`, rec);
    if (!fresh) return { duplicated: true, record: await db.get(`payments/${fk(transactionId)}`) };

    await db.transaction(`users/${fk(uid)}`, (u) => {
      if (!u) return undefined;
      if (order.kind === "tokens") return { ...u, wallet: (Number(u.wallet) || 0) + rec.tokens };
      // ต่ออายุแพ็กเกจเดิม = บวกต่อจากวันหมดอายุ ; เปลี่ยนแพ็กเกจ = เริ่มใหม่วันนี้ + รีเซ็ตโควตาเดือนนี้
      const same = isActivePlan(u) && u.plan.id === order.planId && (order.planId !== CUSTOM_PLAN_ID || Number(u.plan.tokens) === rec.tokens);
      const start = same ? new Date(u.plan.expiresAt) : now;
      const end = new Date(start);
      if (order.cycle === "yearly") end.setFullYear(end.getFullYear() + 1);
      else end.setMonth(end.getMonth() + 1);
      return {
        ...u,
        plan: { id: order.planId, cycle: order.cycle, startedAt: same ? u.plan.startedAt : now.toISOString(), expiresAt: end.toISOString(), ...(order.planId === CUSTOM_PLAN_ID ? { tokens: rec.tokens } : {}) },
        quota: same ? u.quota : { period: monthKey(), used: 0 },
      };
    });
    if (order.kind === "tokens") await db.put(`wallet_tx/${fk(uid)}/${newId("w")}`, { type: "purchase", tokens: rec.tokens, note: rec.itemName, ref: transactionId, at: rec.createdAt });
    bumpStats({ revenue: rec.amount, orders: 1 });
    return { duplicated: false, record: rec };
  },

  /** ยกเลิกต่ออายุ = ใช้ได้ถึงวันหมดอายุ แล้วกลับเป็น Free */
  async cancelRenewal(uid) {
    await db.patch(`users/${fk(uid)}/plan`, { cancelled: true });
  },
};
